import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { qk } from "@/lib/query-keys";
import { toastErrorApi, toastExito } from "@/lib/toast";
import type { OrdenCompraInput } from "@/lib/types";
import {
  actualizarOrdenCompra,
  crearOrdenCompra,
  darDeBajaOrdenCompra,
  listarOrdenesCompra,
  obtenerOrdenCompraPorId,
  reactivarOrdenCompra,
  type ListarOrdenesParams,
} from "../services/ordenes-compra-service";

/**
 * Listado PAGINADO server-side · los params (busqueda/page/pageSize) forman
 * parte de la key (local · extiende qk.adminOrdenesCompra para que los
 * invalidate de las mutations sigan alcanzando por prefijo).
 */
export const ordenesCompraAdminQueryOptions = (params: ListarOrdenesParams = {}) =>
  queryOptions({
    queryKey: [...qk.adminOrdenesCompra, "paginado", params] as const,
    queryFn: () => listarOrdenesCompra(params),
    placeholderData: keepPreviousData,
  });

export const useOrdenesCompraAdmin = (params: ListarOrdenesParams = {}) =>
  useQuery(ordenesCompraAdminQueryOptions(params));

/** Detalle de una VERSIÓN de OC por su id (Guid) · para la ruta de edición. */
export const ordenCompraAdminQueryOptions = (id: string) =>
  queryOptions({
    queryKey: qk.adminOrdenCompra(id),
    queryFn: () => obtenerOrdenCompraPorId(id),
  });

export const useOrdenCompraAdmin = (id: string) =>
  useSuspenseQuery(ordenCompraAdminQueryOptions(id));

export function useCrearOrdenCompra(opts?: { onOk?: () => void }) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ clave, input }: { clave: string; input: OrdenCompraInput }) =>
      crearOrdenCompra(clave, input),
    onSuccess: (oc) => {
      toastExito({ title: `Orden de compra ${oc.clave} creada`, message: "Guardada." });
      void qc.invalidateQueries({ queryKey: qk.adminOrdenesCompra });
      opts?.onOk?.();
    },
    onError: toastErrorApi,
  });
}

export function useActualizarOrdenCompra(opts?: { onOk?: () => void }) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ clave, input }: { clave: string; input: OrdenCompraInput }) =>
      actualizarOrdenCompra(clave, input),
    onSuccess: (oc) => {
      toastExito({ title: `Orden de compra ${oc.clave} actualizada`, message: "Cambios guardados." });
      void qc.invalidateQueries({ queryKey: qk.adminOrdenesCompra });
      void qc.invalidateQueries({ queryKey: qk.adminOrdenCompra(oc.clave) });
      opts?.onOk?.();
    },
    onError: toastErrorApi,
  });
}

export function useDarDeBajaOrdenCompra() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => darDeBajaOrdenCompra(id),
    onSuccess: () => {
      toastExito({
        title: "Orden de compra dada de baja",
        message: "Ya no está disponible para consumo · se conserva el histórico.",
      });
      void qc.invalidateQueries({ queryKey: qk.adminOrdenesCompra });
    },
    onError: toastErrorApi,
  });
}

export function useReactivarOrdenCompra() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => reactivarOrdenCompra(id),
    onSuccess: () => {
      toastExito({ title: "Orden de compra reactivada", message: "Disponible de nuevo." });
      void qc.invalidateQueries({ queryKey: qk.adminOrdenesCompra });
    },
    onError: toastErrorApi,
  });
}
